import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Database, Sparkles, Plus, Trash2, Loader2, Lock, ArrowLeft, X, Pencil, Check } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";

const FIELD_TYPE_LABELS = {
  text: "Text",
  long_text: "Long text",
  number: "Number",
  date: "Date",
  checkbox: "Checkbox",
  single_select: "Single select",
  url: "URL",
  email: "Email",
};

export default function BusinessData() {
  const [collections, setCollections] = useState([]);
  const [locked, setLocked] = useState(false);
  const [currentPlan, setCurrentPlan] = useState("free");
  const [loading, setLoading] = useState(true);
  const [openCollection, setOpenCollection] = useState(null); // full collection object
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/data/collections");
      setCollections(data.collections || []);
      setLocked(!!data.locked);
      setCurrentPlan(data.current_plan || "free");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load Data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/data/collections/${id}`);
      setOpenCollection(data.collection);
    } catch {
      toast.error("Could not open collection.");
    }
  };

  const removeCollection = async (id) => {
    if (!window.confirm("Delete this tracker and all its records?")) return;
    try {
      await api.delete(`/data/collections/${id}`);
      setCollections((c) => c.filter((x) => x.id !== id));
      if (openCollection?.id === id) setOpenCollection(null);
      toast.success("Tracker deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (locked) return <LockedState currentPlan={currentPlan} />;

  if (openCollection) {
    return (
      <CollectionDetail
        collection={openCollection}
        onBack={() => { setOpenCollection(null); load(); }}
        onDelete={() => removeCollection(openCollection.id)}
      />
    );
  }

  return (
    <div data-testid="business-data-page">
      <Seo title="Business Data" description="AI-built custom trackers for business-specific data." path="/app/data" />
      <header className="mb-6 flex items-start justify-between gap-3">
        <div>
          <p className="eyebrow text-brand-teal">Business Data</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Custom trackers, built for you.</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            For business data that doesn't belong in CRM, Projects, or Tasks — e.g., candidates, vendors, campaigns, inventory.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"
          data-testid="business-data-new"
        >
          <Sparkles className="h-4 w-4" /> New tracker
        </button>
      </header>

      {loading ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : collections.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center">
          <Database className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">No trackers yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Describe what you want to track — Workspace will design the schema for you.</p>
          <button
            onClick={() => setCreating(true)}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"
            data-testid="business-data-empty-new"
          >
            <Sparkles className="h-4 w-4" /> Create your first tracker
          </button>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {collections.map((c) => (
            <button
              key={c.id}
              onClick={() => openDetail(c.id)}
              className="group rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-primary"
              data-testid={`business-data-card-${c.id}`}
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-teal/10 text-brand-teal">
                  <Database className="h-4 w-4" />
                </span>
                <p className="flex-1 font-display text-sm font-semibold">{c.name}</p>
              </div>
              {c.description && <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{c.description}</p>}
              <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{c.fields?.length || 0} fields</span>
                <span>{c.record_count || 0} records</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <CreateTrackerModal open={creating} onClose={() => setCreating(false)} onCreated={(coll) => { setCreating(false); setCollections((all) => [{ ...coll, record_count: 0 }, ...all]); openDetail(coll.id); }} />
    </div>
  );
}

function LockedState({ currentPlan }) {
  return (
    <div data-testid="business-data-locked">
      <Seo title="Business Data" description="AI-built custom trackers for business-specific data." path="/app/data" />
      <div className="rounded-2xl border border-border bg-card p-8 text-center">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Lock className="h-6 w-6" />
        </span>
        <h1 className="mt-3 font-display text-2xl font-semibold">Business Data</h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          Create business-specific trackers and structured data with AI — for things that don't belong in CRM, Projects, or Tasks.
        </p>
        <p className="mt-3 text-xs text-muted-foreground">Available on Growth and above. Your current plan: <b>{currentPlan}</b>.</p>
        <div className="mt-5 flex justify-center gap-2">
          <Link to="/pricing" className="rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white">View plans</Link>
          <Link to="/app/founding" className="rounded-full border border-border px-5 py-2 text-sm font-semibold">Request Launch Offer</Link>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Create tracker — AI schema builder                                  */
/* ------------------------------------------------------------------ */
function CreateTrackerModal({ open, onClose, onCreated }) {
  const [prompt, setPrompt] = useState("");
  const [draft, setDraft] = useState(null); // { collection: {...} }
  const [refusal, setRefusal] = useState(null); // { redirect, message }
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setPrompt(""); setDraft(null); setRefusal(null); setBusy(false); setSaving(false);
    }
  }, [open]);

  const proposeSchema = async () => {
    if (!prompt.trim()) return;
    setBusy(true); setDraft(null); setRefusal(null);
    try {
      const { data } = await api.post("/data/collections/draft", { prompt: prompt.trim() });
      if (data.ok === false) setRefusal({ redirect: data.redirect, message: data.message });
      else setDraft({ collection: data.collection });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI couldn't produce a schema.");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!draft?.collection) return;
    setSaving(true);
    try {
      const { data } = await api.post("/data/collections", draft.collection);
      toast.success("Tracker created.");
      onCreated?.(data.collection);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create tracker.");
    } finally { setSaving(false); }
  };

  const updateColl = (changes) => setDraft((d) => ({ ...d, collection: { ...d.collection, ...changes } }));
  const updateField = (i, changes) => {
    const fields = draft.collection.fields.map((f, idx) => (idx === i ? { ...f, ...changes } : f));
    updateColl({ fields });
  };
  const removeField = (i) => updateColl({ fields: draft.collection.fields.filter((_, idx) => idx !== i) });

  if (!open) return null;

  const redirectHref = {
    companies: "/app/sales", contacts: "/app/sales", deals: "/app/sales",
    projects: "/app/projects", tasks: "/app/work", goals: "/app/goals",
  }[refusal?.redirect] || "/app";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="business-data-create-modal">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-border bg-card shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Tracker Builder
            </div>
            <h2 className="mt-1 text-lg font-semibold">Describe what you want to track</h2>
            <p className="mt-1 text-xs text-muted-foreground">AI proposes a schema. You review, edit, then approve. No data is created silently.</p>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted" data-testid="business-data-create-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., Track vendors I buy from — name, category, contact person, phone, payment terms, notes"
              rows={3}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              data-testid="business-data-prompt"
            />
            <button
              onClick={proposeSchema}
              disabled={busy || !prompt.trim()}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
              data-testid="business-data-propose"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {draft ? "Regenerate schema" : "Propose schema"}
            </button>
          </div>

          {refusal && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 text-sm" data-testid="business-data-refusal">
              <p className="font-medium text-foreground">Workspace already has this covered.</p>
              <p className="mt-1 text-muted-foreground">{refusal.message}</p>
              <Link to={redirectHref} className="mt-2 inline-flex items-center gap-1 rounded-full brand-gradient-bg px-3 py-1 text-xs font-semibold text-white">
                Open {refusal.redirect}
              </Link>
            </div>
          )}

          {draft?.collection && (
            <div className="space-y-3" data-testid="business-data-draft">
              <div className="rounded-xl border border-border p-3">
                <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Tracker name</label>
                <input
                  value={draft.collection.name}
                  onChange={(e) => updateColl({ name: e.target.value })}
                  className="mt-1 w-full rounded-md border border-border bg-transparent px-2 py-1 text-sm font-semibold"
                />
                <label className="mt-3 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Description</label>
                <input
                  value={draft.collection.description || ""}
                  onChange={(e) => updateColl({ description: e.target.value })}
                  className="mt-1 w-full rounded-md border border-border bg-transparent px-2 py-1 text-sm"
                />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Fields</p>
                <div className="mt-2 space-y-2">
                  {draft.collection.fields.map((f, i) => (
                    <div key={f.key + i} className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-2">
                      <input
                        value={f.name}
                        onChange={(e) => updateField(i, { name: e.target.value })}
                        className="flex-1 min-w-[160px] rounded-md border border-transparent bg-transparent px-2 py-1 text-sm focus:border-border"
                      />
                      <select
                        value={f.type}
                        onChange={(e) => updateField(i, { type: e.target.value })}
                        className="rounded-md border border-border bg-transparent px-2 py-1 text-xs"
                      >
                        {Object.entries(FIELD_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      {f.type === "single_select" && (
                        <input
                          value={(f.options || []).join(", ")}
                          onChange={(e) => updateField(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                          placeholder="option1, option2"
                          className="w-full rounded-md border border-border bg-transparent px-2 py-1 text-xs"
                        />
                      )}
                      <label className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(i, { required: e.target.checked })} /> required
                      </label>
                      <button onClick={() => removeField(i)} className="rounded-md p-1 text-muted-foreground hover:text-destructive" aria-label="Remove">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          <button onClick={onClose} className="rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted">Cancel</button>
          <button
            onClick={save}
            disabled={!draft?.collection || saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            data-testid="business-data-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Create tracker
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Collection detail view                                              */
/* ------------------------------------------------------------------ */
function CollectionDetail({ collection, onBack, onDelete }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // record being edited, or "new"
  const fields = collection.fields || [];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/data/collections/${collection.id}/records`);
      setRecords(data.records || []);
    } catch { toast.error("Could not load records."); }
    finally { setLoading(false); }
  }, [collection.id]);

  useEffect(() => { load(); }, [load]);

  const saveRecord = async (values, existingId) => {
    try {
      if (existingId) {
        const { data } = await api.patch(`/data/collections/${collection.id}/records/${existingId}`, { values });
        setRecords((r) => r.map((x) => (x.id === existingId ? data.record : x)));
      } else {
        const { data } = await api.post(`/data/collections/${collection.id}/records`, { values });
        setRecords((r) => [data.record, ...r]);
      }
      setEditing(null);
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    }
  };

  const deleteRecord = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/data/collections/${collection.id}/records/${id}`);
      setRecords((r) => r.filter((x) => x.id !== id));
    } catch { toast.error("Could not delete."); }
  };

  return (
    <div data-testid="business-data-detail">
      <Seo title={collection.name} description={collection.description || "Business tracker"} path={`/app/data/${collection.id}`} />
      <header className="mb-5">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="business-data-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to trackers
        </button>
        <div className="mt-2 flex items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold">{collection.name}</h1>
            {collection.description && <p className="mt-1 text-sm text-muted-foreground">{collection.description}</p>}
            <p className="mt-1 text-xs text-muted-foreground">{fields.length} fields · {records.length} records</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing("new")} className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-3 py-1.5 text-xs font-semibold text-white" data-testid="record-add">
              <Plus className="h-3.5 w-3.5" /> Add record
            </button>
            <button onClick={onDelete} className="rounded-full border border-destructive/50 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10" data-testid="collection-delete">
              Delete tracker
            </button>
          </div>
        </div>
      </header>

      {loading ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : records.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No records yet. Add your first row.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                {fields.map((f) => (
                  <th key={f.key} className="px-3 py-2 font-semibold">{f.name}</th>
                ))}
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {records.map((r) => (
                <tr key={r.id} className="hover:bg-muted/30" data-testid={`record-row-${r.id}`}>
                  {fields.map((f) => (
                    <td key={f.key} className="px-3 py-2 align-top">
                      {renderCell(f, r.values?.[f.key])}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditing(r)} className="mr-1 rounded-md p-1 text-muted-foreground hover:text-foreground" aria-label="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => deleteRecord(r.id)} className="rounded-md p-1 text-muted-foreground hover:text-destructive" aria-label="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <RecordEditor
          fields={fields}
          record={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={(vals) => saveRecord(vals, editing === "new" ? null : editing.id)}
        />
      )}
    </div>
  );
}

function renderCell(f, v) {
  if (v === undefined || v === null || v === "") return <span className="text-muted-foreground">—</span>;
  if (f.type === "checkbox") return v ? <Check className="h-4 w-4 text-brand-teal" /> : <span className="text-muted-foreground">—</span>;
  if (f.type === "url") return <a href={v} target="_blank" rel="noreferrer" className="text-primary underline">{String(v).slice(0, 40)}</a>;
  if (f.type === "email") return <a href={`mailto:${v}`} className="text-primary underline">{v}</a>;
  if (f.type === "long_text") return <span className="line-clamp-2 text-xs">{v}</span>;
  return <span>{String(v)}</span>;
}

function RecordEditor({ fields, record, onClose, onSave }) {
  const initial = useMemo(() => {
    const o = {};
    fields.forEach((f) => { o[f.key] = record?.values?.[f.key] ?? (f.type === "checkbox" ? false : ""); });
    return o;
  }, [fields, record]);
  const [values, setValues] = useState(initial);
  useEffect(() => setValues(initial), [initial]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="record-editor">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-lg font-semibold">{record ? "Edit record" : "New record"}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-medium text-muted-foreground">
                {f.name}{f.required && <span className="text-destructive"> *</span>}
              </label>
              {f.type === "long_text" ? (
                <textarea
                  value={values[f.key] || ""}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              ) : f.type === "single_select" ? (
                <select
                  value={values[f.key] || ""}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === "checkbox" ? (
                <label className="mt-1 inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!values[f.key]} onChange={(e) => setValues({ ...values, [f.key]: e.target.checked })} /> Yes
                </label>
              ) : (
                <input
                  type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "email" ? "email" : f.type === "url" ? "url" : "text"}
                  value={values[f.key] ?? ""}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button onClick={onClose} className="rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted">Cancel</button>
          <button onClick={() => onSave(values)} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" data-testid="record-save">
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
